import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  Lock,
  Pencil,
  Save,
  Send,
  Upload,
  UserCheck,
  Wrench,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useBreadcrumb } from '@/components/common/BreadcrumbContext';
import LockedCcChips from '@/components/common/LockedCcChips';
import { StateCitySelect } from '@/components/common/StateCitySelect';
import { StatusChip, type ChipTone } from '@/components/sales/StatusChip';
import NumberStepper from '@/components/ui/NumberStepper';
import { useToast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select-menu';
import { Textarea } from '@/components/ui/textarea';
import { SETTLED_PAYMENT_MODES, paymentModesFor } from '@/features/sales-rm/leadForm';
import useAuth from '@/hooks/useAuth';
import {
  useBillingClient,
  useClaimBilling,
  useCompleteBilling,
  useNotifyService,
  useRecordInvoice,
  useReleaseBilling,
  useSendBillingCorrection,
  useSendClientInvoice,
  useUpdateVerificationData,
  useVerifyBilling,
} from '@/hooks/useBilling';
import { inr } from '@/lib/sales-metrics';
import {
  billingService,
  fileToBase64,
  type BillingClientDetail,
  type BillingEditableClient,
  type BillingEditableData,
  type BillingEditableInstallment,
  type BillingEditableProduct,
  type BillingEditableSales,
  type BillingVerificationPatch,
} from '@/services/billing.service';
import { errorMessage } from '@/utils/apiError';
import cn from '@/utils/cn';
import { QUERY_KEYS, RENEWAL_FREQUENCIES, ROUTES } from '@/utils/constants';

const INVOICE_TEMPLATE_SLUG = 'tax-invoice-draft';

const STEPS = [
  { key: 'verify', title: 'Billing Verification', short: 'Verify data from Sales RM' },
  { key: 'invoice', title: 'Generate Invoice', short: 'Upload the invoice PDF' },
  { key: 'notify', title: 'Service Team Notification', short: 'Notify the service queue' },
  { key: 'send', title: 'Send Invoice To Client', short: 'Email the invoice' },
  { key: 'complete', title: 'Billing Complete', short: 'Mark billing done' },
] as const;

type StepKey = (typeof STEPS)[number]['key'];
type StepStatus = 'completed' | 'current' | 'pending' | 'blocked';

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const doneFlags = (d: BillingClientDetail): Record<StepKey, boolean> => ({
  verify: !!d.state5.verifiedAt,
  invoice: !!d.state5.invoiceRecordedAt,
  notify: !!d.state5.serviceNotifiedAt,
  send: !!d.state5.clientInvoiceSentAt,
  complete: !!d.state5.completedAt,
});

/**
 * The steps this installment actually has. Two independent rules trim the
 * ladder:
 *
 *  - A 'Due' installment reached billing with no money against it (ADR-053), so
 *    there is nothing to invoice and nothing to send the client. It is invoiced
 *    later, when the payment is recorded, which puts it back in the queue as a
 *    fresh, full-length workflow.
 *  - Only the FIRST installment notifies the Service team. Service is activated
 *    once per order; installments 2 and 3 pay for that same subscription, so
 *    notifying again would ask the Service team to switch on something already
 *    running and add a second row to their queue for work that does not exist.
 */
const stepsFor = (d: BillingClientDetail): readonly (typeof STEPS)[number][] => {
  const skipped = new Set<StepKey>();
  if (d.state5.dueBilling) {
    skipped.add('invoice');
    skipped.add('send');
  }
  if (!d.state5.notifiesService) skipped.add('notify');
  return STEPS.filter((s) => !skipped.has(s.key));
};

const firstIncompleteIndex = (d: BillingClientDetail): number => {
  const flags = doneFlags(d);
  const steps = stepsFor(d);
  const idx = steps.findIndex((s) => !flags[s.key]);
  return idx === -1 ? steps.length - 1 : idx;
};

/**
 * The step that follows `key` in THIS workflow, skipping the ones it does not
 * run. Derived rather than hard-coded per step, so a finished step always lands
 * on one that exists no matter which rules trimmed the ladder.
 */
const nextStepAfter = (d: BillingClientDetail, key: StepKey): StepKey => {
  const steps = stepsFor(d);
  const idx = steps.findIndex((s) => s.key === key);
  return steps[Math.min(idx + 1, steps.length - 1)].key;
};

const STEP_ICON: Record<StepStatus, ComponentType<{ className?: string }>> = {
  completed: CheckCircle2,
  current: Loader2,
  pending: AlertCircle,
  blocked: Lock,
};

const STEP_DOT: Record<StepStatus, string> = {
  completed: 'bg-emerald-500 text-white',
  current: 'bg-yellow-400 text-black',
  pending: 'bg-muted text-muted-foreground',
  blocked: 'bg-muted text-muted-foreground',
};

// Lead-level billing status tone. 'Partially Billed' and 'Billing Complete' are
// both green (nothing to action right now); pending stages are info/warning.
const billingLeadTone = (status: string): ChipTone => {
  if (status === 'Billing Complete' || status === 'Partially Billed') return 'success';
  if (status === 'Pending Verification') return 'warning';
  return 'info';
};

// Per-installment billing label tone in the Payment Validation card.
const installmentBillingTone = (label: string): ChipTone => {
  if (label === 'Billed') return 'success';
  if (label === 'Due') return 'warning';
  return 'neutral';
};

// ─── Presentational helpers ──────────────────────────────────────────────────
const HeaderCell = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={cn('break-words text-sm font-semibold text-foreground', mono && 'font-mono')}>
      {value}
    </p>
  </div>
);

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="min-w-0 rounded-lg border border-border bg-background p-4">
    <p className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</p>
    {children}
  </div>
);

// break-words (overflow-wrap: break-word) lets long unbroken strings like
// emails wrap instead of overflowing their card; min-w-0 is required on the
// grid cell itself, since a grid item's default min-width:auto otherwise
// forces the row wide enough to fit the content unwrapped.
const ReadOnly = ({ label, value }: { label: string; value: string }) => (
  <div className="min-w-0 space-y-1">
    <p className="text-[11px] font-semibold text-muted-foreground">{label}</p>
    <p className="break-words text-sm font-semibold">{value}</p>
  </div>
);

// ─── Per-step audit metadata ─────────────────────────────────────────────────
// The Activity Timeline is merged directly into the workflow stepper (no
// separate column): each completed step shows when it happened, who did it, and
// the concrete data recorded at that step (invoice number/amount, service queue
// ref, recipient, notes).
interface StepMeta {
  at: string | null;
  by: string | null;
  detail?: string;
}

const stepMetaFor = (detail: BillingClientDetail, key: StepKey): StepMeta => {
  const s = detail.state5;
  switch (key) {
    case 'verify':
      return { at: s.verifiedAt, by: s.verifiedByName, detail: s.verifyNotes || undefined };
    case 'invoice':
      return {
        at: s.invoiceRecordedAt,
        by: s.invoiceRecordedByName,
        detail:
          s.invoiceNumber && s.invoiceAmount != null
            ? `${s.invoiceNumber} · ${inr(s.invoiceAmount)}`
            : undefined,
      };
    case 'notify':
      return {
        at: s.serviceNotifiedAt,
        by: s.serviceNotifiedByName,
        detail: s.serviceQueueRef ? `Queue ${s.serviceQueueRef}` : undefined,
      };
    case 'send':
      return {
        at: s.clientInvoiceSentAt,
        by: s.clientInvoiceSentByName,
        detail: s.clientInvoiceSentAt ? `Emailed to ${detail.email}` : undefined,
      };
    case 'complete':
      return {
        at: s.completedAt,
        by: s.completedByName,
        detail: s.completedAt ? 'Handed to Service Team' : undefined,
      };
    default:
      return { at: null, by: null };
  }
};

// ─── Step 1: Verify ──────────────────────────────────────────────────────────
interface StepProps {
  detail: BillingClientDetail;
  onDone: () => void;
  locked: boolean;
}

// A labelled input used inside the four editable verification blocks. Falls
// back to the same read-only presentation when the block is not in edit mode,
// so the layout never jumps between the two states.
interface EditFieldProps {
  label: string;
  value: string;
  editing: boolean;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}

const EditField = ({ label, value, editing, onChange, placeholder, type }: EditFieldProps) => {
  if (!editing) return <ReadOnly label={label} value={value || '-'} />;
  return (
    <div className="min-w-0 space-y-1">
      <p className="text-[11px] font-semibold text-muted-foreground">{label}</p>
      <Input
        value={value}
        type={type}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 text-sm"
        aria-label={label}
      />
    </div>
  );
};

// The four verification blocks in editable form. Kept as one draft object so
// "Save Corrections" can diff it against the server values and send only what
// actually changed.
interface VerifyDraft {
  client: BillingEditableClient;
  sales: BillingEditableSales;
  products: BillingEditableProduct[];
  installments: BillingEditableInstallment[];
}

const draftFrom = (e: BillingEditableData): VerifyDraft => ({
  client: { ...e.client },
  sales: { ...e.sales },
  products: e.products.map((p) => ({ ...p })),
  installments: e.installments.map((i) => ({ ...i })),
});

const toNullableInt = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? Math.round(n) : null;
};

// Diff the draft against what the server sent. Only changed keys are included,
// so the server logs (and the RM is told about) exactly what the biller touched.
const buildPatch = (base: BillingEditableData, draft: VerifyDraft): BillingVerificationPatch => {
  const patch: BillingVerificationPatch = {};

  const client: Partial<BillingEditableClient> = {};
  (Object.keys(draft.client) as Array<keyof BillingEditableClient>).forEach((key) => {
    if (draft.client[key].trim() !== base.client[key]) client[key] = draft.client[key].trim();
  });
  if (Object.keys(client).length > 0) patch.client = client;

  const sales: Partial<BillingEditableSales> = {};
  (Object.keys(draft.sales) as Array<keyof BillingEditableSales>).forEach((key) => {
    if (draft.sales[key] !== base.sales[key]) sales[key] = draft.sales[key];
  });
  if (Object.keys(sales).length > 0) patch.sales = sales;

  const products = draft.products
    .filter((p, idx) => {
      const was = base.products[idx];
      return (
        was &&
        (p.customPrice !== was.customPrice ||
          p.termYears !== was.termYears ||
          p.discountMode !== was.discountMode ||
          p.discountValue !== was.discountValue)
      );
    })
    .map((p) => ({
      productCode: p.productCode,
      customPrice: p.customPrice,
      termYears: p.termYears,
      discountMode: p.discountMode,
      discountValue: p.discountValue,
    }));
  if (products.length > 0) patch.products = products;

  // The server validates the schedule as a whole (contiguous 1..N summing to
  // the payable total), so any single change sends the complete schedule.
  const scheduleChanged = draft.installments.some((i, idx) => {
    const was = base.installments[idx];
    return (
      !was ||
      i.amount !== was.amount ||
      i.dueDate !== was.dueDate ||
      i.transactionNo !== was.transactionNo ||
      i.mode !== was.mode
    );
  });
  if (scheduleChanged) {
    patch.installments = draft.installments.map((i) => ({
      installmentNumber: i.installmentNumber,
      amount: i.amount,
      dueDate: i.dueDate,
      transactionNo: i.transactionNo.trim() || null,
      mode: i.mode || null,
    }));
  }
  return patch;
};

// ─── Correction notice to the Sales RM ───────────────────────────────────────
// Billing owns the invoice but the Sales RM owns the data. Anything corrected
// here must be emailed back to them (with a before/after table) before the
// record can be verified - the server enforces the same gate.
const CorrectionNotice = ({ detail }: { detail: BillingClientDetail }) => {
  const { toast } = useToast();
  const send = useSendBillingCorrection(detail.id, detail.orderId);
  const pending = detail.correction.entries.filter((e) => e.notifiedAt == null);

  const preview = useQuery({
    queryKey: [...QUERY_KEYS.BILLING.BY_ID(detail.id), detail.orderId, 'correction-preview'],
    queryFn: () => billingService.previewCorrectionEmail(detail.id, detail.orderId),
    enabled: pending.length > 0,
  });

  const rmEmail = detail.rmEmail && detail.rmEmail !== '-' ? detail.rmEmail : '';
  const [to, setTo] = useState(rmEmail);
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');

  useEffect(() => {
    if (preview.data?.defaultTo && !to) setTo(preview.data.defaultTo);
  }, [preview.data, to]);
  useEffect(() => {
    if (preview.data && !subject) setSubject(preview.data.subject);
  }, [preview.data, subject]);

  if (pending.length === 0) {
    if (detail.correction.entries.length === 0) return null;
    return (
      <div className="rounded-lg border border-success/40 bg-success-tint p-3 text-sm text-ink">
        {`${detail.correction.entries.length} correction(s) made and sent to the Sales RM `}
        {`${fmtDateTime(detail.correction.notifiedAt)}`}
        {detail.correction.notifiedByName ? ` by ${detail.correction.notifiedByName}.` : '.'}
      </div>
    );
  }

  const submit = () => {
    if (!preview.data) return;
    const ccList = cc
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    send.mutate(
      {
        to: [to.trim()],
        cc: ccList.length ? ccList : undefined,
        subject: subject || preview.data.subject,
        bodyHtml: preview.data.bodyHtml,
        bodyText: preview.data.bodyText,
      },
      {
        onSuccess: () => toast.success(`Correction summary sent to ${to.trim()}`),
        onError: (error) =>
          toast.error(errorMessage(error, 'Could not send the correction summary')),
      },
    );
  };

  return (
    <Section title="Corrections - Notify Sales RM">
      <div className="mb-3 rounded-md border border-warning/40 bg-warning-tint p-3 text-sm text-ink">
        <p className="font-bold">
          {`${pending.length} correction${pending.length === 1 ? '' : 's'} not yet sent`}
        </p>
        <p>
          Send this summary to the Sales RM who handed the record over. Verification stays blocked
          until it goes out.
        </p>
      </div>

      <div className="mb-3 overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="bg-muted/40 text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Field</th>
              <th className="px-3 py-2">Was</th>
              <th className="px-3 py-2">Corrected To</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((e) => (
              <tr key={`${e.field}-${e.at}`} className="border-t border-border">
                <td className="px-3 py-1.5 font-semibold">{e.label}</td>
                <td className="px-3 py-1.5 text-muted-foreground line-through">{e.from}</td>
                <td className="px-3 py-1.5 font-semibold">{e.to}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>To (Sales RM)</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>CC (comma separated)</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} />
            <LockedCcChips category="internal" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Subject</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
      </div>

      <div className="mt-3 space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          Preview
        </p>
        {preview.isLoading && <p className="text-sm text-muted-foreground">Loading preview…</p>}
        {preview.isError && (
          <p className="text-sm text-destructive">
            {errorMessage(preview.error, 'Could not load the correction email preview.')}
          </p>
        )}
        {preview.data && (
          <iframe
            title="Correction email preview"
            className="h-[320px] w-full rounded-md border border-border bg-white"
            srcDoc={preview.data.bodyHtml}
          />
        )}
      </div>

      <div className="mt-3 flex justify-end">
        <Button size="sm" onClick={submit} disabled={!preview.data || !to.trim() || send.isPending}>
          <Send className="mr-1 h-3.5 w-3.5" />
          {send.isPending ? 'Sending…' : 'Send Correction To Sales RM'}
        </Button>
      </div>
    </Section>
  );
};

const StepVerify = ({ detail, onDone, locked }: StepProps) => {
  const { toast } = useToast();
  const verify = useVerifyBilling(detail.id, detail.orderId);
  const save = useUpdateVerificationData(detail.id, detail.orderId);
  const [notes, setNotes] = useState(detail.state5.verifyNotes ?? '');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<VerifyDraft>(() => draftFrom(detail.editable));
  const done = !!detail.state5.verifiedAt;
  const pending = detail.pendingAmount;
  const { editable } = detail;
  const canEdit = editable.canEdit && !locked;
  const blockedByCorrections = detail.correction.pendingCount > 0;

  // Re-seed the draft whenever the server sends fresh values (after a save, or
  // after another biller's change lands) - never while the biller is typing.
  useEffect(() => {
    if (!editing) setDraft(draftFrom(detail.editable));
  }, [detail.editable, editing]);

  const patchClient = (key: keyof BillingEditableClient, value: string) =>
    setDraft((d) => ({ ...d, client: { ...d.client, [key]: value } }));
  const patchSales = (key: keyof BillingEditableSales, value: string) =>
    setDraft((d) => ({ ...d, sales: { ...d.sales, [key]: value } }));
  const patchProduct = (idx: number, changes: Partial<BillingEditableProduct>) =>
    setDraft((d) => ({
      ...d,
      products: d.products.map((p, i) => (i === idx ? { ...p, ...changes } : p)),
    }));
  const patchInstallment = (idx: number, changes: Partial<BillingEditableInstallment>) =>
    setDraft((d) => ({
      ...d,
      installments: d.installments.map((r, i) => (i === idx ? { ...r, ...changes } : r)),
    }));

  // Every <option> must be a real element: `Select` (select-menu.tsx) reads
  // `.props.value` off each child, and React.Children.toArray keeps strings - so
  // a conditional child rendered with `&&` on a possibly-empty string would leak
  // '' into the list and crash it. Build the list as data instead.
  const bankChoices =
    draft.sales.selectedBankLabel && !editable.bankOptions.includes(draft.sales.selectedBankLabel)
      ? [...editable.bankOptions, draft.sales.selectedBankLabel]
      : editable.bankOptions;

  const draftScheduleTotal = draft.installments.reduce((s, i) => s + i.amount, 0);
  const scheduleMismatch =
    editable.payableTotal != null && draftScheduleTotal !== editable.payableTotal;

  const saveCorrections = () => {
    const patch = buildPatch(editable, draft);
    if (Object.keys(patch).length === 0) {
      toast.info('Nothing changed');
      setEditing(false);
      return;
    }
    save.mutate(patch, {
      onSuccess: () => {
        toast.success('Details corrected - now email the summary to the Sales RM');
        setEditing(false);
      },
      onError: (error) => toast.error(errorMessage(error, 'Could not save the corrections')),
    });
  };

  const submit = () => {
    verify.mutate(notes.trim() || undefined, {
      onSuccess: () => {
        toast.success('Billing verified');
        onDone();
      },
      onError: (error) => toast.error(errorMessage(error, 'Could not verify billing')),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-extrabold">Billing Verification</h2>
          <p className="text-sm text-muted-foreground">
            {canEdit
              ? 'Review the data handed over by Sales RM. Correct anything that is wrong - changes are saved to the client record and emailed back to the RM.'
              : 'Review the data handed over by Sales RM.'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canEdit && !editing && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="mr-1 h-3.5 w-3.5" />
              Edit Details
            </Button>
          )}
          {editing && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft(draftFrom(editable));
                  setEditing(false);
                }}
                disabled={save.isPending}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={saveCorrections} disabled={save.isPending}>
                <Save className="mr-1 h-3.5 w-3.5" />
                {save.isPending ? 'Saving…' : 'Save Corrections'}
              </Button>
            </>
          )}
          {!editing && (
            <Button
              size="sm"
              onClick={submit}
              disabled={verify.isPending || locked || blockedByCorrections}
            >
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
              {done ? 'Verified' : 'Verify Billing'}
            </Button>
          )}
        </div>
      </div>

      {blockedByCorrections && !editing && (
        <div className="rounded-lg border border-warning/40 bg-warning-tint p-3 text-sm text-ink">
          Email the correction summary to the Sales RM before verifying.
        </div>
      )}

      {/* "Partial" only when something was actually collected. A Due order has
          nothing against it, so calling it partial - and promising further
          installments that do not exist - would be plainly wrong (ADR-053). */}
      {pending > 0 && (
        <div className="rounded-lg border border-warning/40 bg-warning-tint p-3 text-sm">
          <p className="font-bold text-ink">
            {detail.amountPaid > 0 ? 'Partial payment' : 'Unpaid'}
          </p>
          <p className="text-ink">
            {detail.amountPaid > 0
              ? `${inr(detail.amountPaid)} of ${inr(detail.total)} collected · `
              : `Nothing collected yet against ${inr(detail.total)} · `}
            <span className="font-bold">{inr(pending)}</span>
            {detail.amountPaid > 0
              ? ' still due. Billing can proceed; remaining installments are handed over as they are collected.'
              : " outstanding. Billing can proceed so the client's service starts; the invoice follows once the payment is recorded."}
          </p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Section title="Client Information">
          <div className="grid grid-cols-2 gap-3">
            <EditField
              label="Client Name"
              editing={editing}
              value={draft.client.arnHolderName}
              onChange={(v) => patchClient('arnHolderName', v)}
            />
            <EditField
              label="ARN"
              editing={editing}
              value={draft.client.arn}
              onChange={(v) => patchClient('arn', v)}
              placeholder="6 digits"
            />
            <EditField
              label="Business Name"
              editing={editing}
              value={draft.client.legalName}
              onChange={(v) => patchClient('legalName', v)}
            />
            <EditField
              label="GSTIN"
              editing={editing}
              value={draft.client.gstin}
              onChange={(v) => patchClient('gstin', v)}
            />
            <EditField
              label="Mobile"
              editing={editing}
              value={draft.client.mobile}
              onChange={(v) => patchClient('mobile', v)}
            />
            <EditField
              label="Email"
              editing={editing}
              value={draft.client.email}
              onChange={(v) => patchClient('email', v)}
              type="email"
            />
            {/* Dropdowns while editing, plain read-only text otherwise (Fix 3).
                Free-typed geography is what produced "Bangalore"/"Bengaluru"/
                "bangalore" as three cities, and a biller correcting a client's
                address is exactly where a fourth spelling gets introduced. */}
            {editing ? (
              <StateCitySelect
                state={draft.client.state}
                city={draft.client.city}
                onStateChange={(v) => patchClient('state', v)}
                onCityChange={(v) => patchClient('city', v)}
                labelClassName="text-[11px] font-semibold text-muted-foreground"
              />
            ) : (
              <>
                <ReadOnly label="State" value={draft.client.state || '-'} />
                <ReadOnly label="City" value={draft.client.city || '-'} />
              </>
            )}
            <div className="col-span-2">
              <EditField
                label="Address"
                editing={editing}
                value={draft.client.address}
                onChange={(v) => patchClient('address', v)}
              />
            </div>
          </div>
        </Section>

        <Section title="Sales RM">
          <div className="grid grid-cols-2 gap-3">
            {/* The RM identity is the audit trail of who handed this over (and
                who receives the correction notice) - it is not a data-entry
                mistake a biller corrects, so it stays read-only. */}
            <ReadOnly label="RM Name" value={detail.rmName} />
            <ReadOnly label="RM Email" value={detail.rmEmail} />
            <ReadOnly label="Quotation Ref" value={detail.quotationReference ?? '-'} />
            {editing ? (
              <div className="min-w-0 space-y-1">
                <p className="text-[11px] font-semibold text-muted-foreground">Bank</p>
                <Select
                  value={draft.sales.selectedBankLabel}
                  onChange={(e) => patchSales('selectedBankLabel', e.target.value)}
                  className="h-8 text-sm"
                  aria-label="Bank shared with the client"
                >
                  <option value="">- None -</option>
                  {bankChoices.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </Select>
              </div>
            ) : (
              <ReadOnly label="Bank" value={draft.sales.selectedBankLabel || '-'} />
            )}
            {/* Cadence the RM set in the payment plan step - it decides how
                often this client is re-billed once the notify-service step
                enables service and seeds the renewal cycles. */}
            {editing ? (
              <div className="min-w-0 space-y-1">
                <p className="text-[11px] font-semibold text-muted-foreground">Renewal Frequency</p>
                <Select
                  value={draft.sales.renewalFrequency}
                  onChange={(e) => patchSales('renewalFrequency', e.target.value)}
                  className="h-8 text-sm"
                  aria-label="Renewal frequency"
                >
                  {RENEWAL_FREQUENCIES.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </Select>
              </div>
            ) : (
              <ReadOnly label="Renewal Frequency" value={draft.sales.renewalFrequency} />
            )}
          </div>
        </Section>

        <Section title="Products">
          {editing ? (
            <div className="space-y-3">
              {draft.products.map((p, idx) => (
                <div key={p.productCode} className="rounded-md border border-border p-2.5">
                  <p className="mb-2 text-sm font-bold">{p.productName}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <p className="text-[11px] font-semibold text-muted-foreground">
                        Term (years)
                      </p>
                      <Select
                        value={p.termYears == null ? '' : String(p.termYears)}
                        onChange={(e) =>
                          patchProduct(idx, {
                            termYears: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                        className="h-8 text-sm"
                        aria-label={`${p.productName} term`}
                      >
                        <option value="">Default</option>
                        {p.termOptions.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[11px] font-semibold text-muted-foreground">
                        Price override (₹)
                      </p>
                      <Input
                        type="number"
                        min={0}
                        value={p.customPrice == null ? '' : String(p.customPrice)}
                        placeholder="Catalogue price"
                        onChange={(e) =>
                          patchProduct(idx, {
                            customPrice: toNullableInt(e.target.value),
                          })
                        }
                        className="h-8 text-sm"
                        aria-label={`${p.productName} price override`}
                      />
                    </div>
                    <div className="space-y-1">
                      <p className="text-[11px] font-semibold text-muted-foreground">
                        Discount type
                      </p>
                      <Select
                        value={p.discountMode ?? ''}
                        onChange={(e) =>
                          patchProduct(idx, {
                            discountMode: (e.target.value || null) as 'amount' | 'percent' | null,
                          })
                        }
                        className="h-8 text-sm"
                        aria-label={`${p.productName} discount type`}
                      >
                        <option value="">None</option>
                        <option value="percent">Percent</option>
                        <option value="amount">Amount</option>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[11px] font-semibold text-muted-foreground">
                        Discount value
                      </p>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={p.discountValue == null ? '' : String(p.discountValue)}
                        disabled={!p.discountMode}
                        onChange={(e) =>
                          patchProduct(idx, {
                            discountValue: e.target.value.trim() ? Number(e.target.value) : null,
                          })
                        }
                        className="h-8 text-sm"
                        aria-label={`${p.productName} discount value`}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground">
                Re-pricing changes the payable total. Correct the installment schedule below in the
                same save so the two stay in step.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-muted-foreground">
                  <th className="pb-1">Product</th>
                  <th className="pb-1">Term</th>
                  <th className="pb-1 text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {detail.products.map((p) => (
                  <tr key={p.name} className="border-t border-border">
                    <td className="py-1.5">{p.name}</td>
                    <td className="py-1.5 text-muted-foreground">{p.duration || '-'}</td>
                    <td className="py-1.5 text-right tabular-nums">{inr(p.netAmount)}</td>
                  </tr>
                ))}
                <tr className="border-t border-border">
                  <td className="py-1.5 text-muted-foreground" colSpan={2}>
                    Subtotal
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {inr(detail.subtotal - detail.discountAmount)}
                  </td>
                </tr>
                <tr>
                  <td
                    className="py-1.5 text-muted-foreground"
                    colSpan={2}
                  >{`GST @ ${detail.gstPercentage}%`}</td>
                  <td className="py-1.5 text-right tabular-nums">{inr(detail.gstAmount)}</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="py-1.5 font-bold" colSpan={2}>
                    Total
                  </td>
                  <td className="py-1.5 text-right font-bold tabular-nums">{inr(detail.total)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Section>

        <Section title="Payment Validation">
          <div className="grid grid-cols-2 gap-3">
            <ReadOnly label="Payment Status" value={detail.paymentStatus} />
            {editing ? (
              <div className="min-w-0 space-y-1">
                <p className="text-[11px] font-semibold text-muted-foreground">Payment Mode</p>
                <Select
                  value={draft.sales.paymentMode}
                  onChange={(e) => patchSales('paymentMode', e.target.value)}
                  className="h-8 text-sm"
                  aria-label="Payment mode"
                >
                  <option value="">- None -</option>
                  {/* Whatever is stored is offered back, even if it is not a
                      mode the biller may newly choose. A controlled select with
                      no matching option renders blank, and the biller picking
                      "None" to escape that would log a correction they never
                      meant - which then BLOCKS verification until they email
                      the Sales RM about it (ADR-046). */}
                  {[
                    ...new Set([draft.sales.paymentMode, ...SETTLED_PAYMENT_MODES].filter(Boolean)),
                  ].map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              </div>
            ) : (
              <ReadOnly label="Payment Mode" value={draft.sales.paymentMode || '-'} />
            )}
            <ReadOnly label="Amount Paid" value={inr(detail.amountPaid)} />
            <ReadOnly label="Pending" value={inr(detail.pendingAmount)} />
          </div>
          <div className="mt-3 overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-[560px] whitespace-nowrap text-sm">
              <thead className="bg-muted/40 text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Due</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Txn No.</th>
                  <th className="px-3 py-2">Payment</th>
                  <th className="px-3 py-2">Billing</th>
                </tr>
              </thead>
              <tbody>
                {detail.installments.map((i, idx) => {
                  const row = draft.installments[idx];
                  return (
                    <tr
                      key={i.installmentNumber}
                      className={cn('border-t border-border', i.isCurrent && 'bg-primary/5')}
                    >
                      <td className="px-3 py-1.5">
                        {i.installmentNumber}
                        {i.isCurrent && (
                          <span className="ml-1 rounded bg-primary px-1 text-[9px] font-bold text-primary-foreground">
                            NOW
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        {editing && row ? (
                          <Input
                            type="date"
                            value={row.dueDate}
                            onChange={(e) => patchInstallment(idx, { dueDate: e.target.value })}
                            className="h-8 w-36 text-sm"
                            aria-label={`Installment ${i.installmentNumber} due date`}
                          />
                        ) : (
                          i.dueDate
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {editing && row ? (
                          <Input
                            type="number"
                            min={1}
                            value={String(row.amount)}
                            onChange={(e) =>
                              patchInstallment(idx, {
                                amount: toNullableInt(e.target.value) ?? 0,
                              })
                            }
                            className="h-8 w-28 text-sm"
                            aria-label={`Installment ${i.installmentNumber} amount`}
                          />
                        ) : (
                          inr(i.amount)
                        )}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-xs">
                        {editing && row ? (
                          <Input
                            value={row.transactionNo}
                            onChange={(e) =>
                              patchInstallment(idx, {
                                transactionNo: e.target.value,
                              })
                            }
                            className="h-8 w-36 text-sm"
                            aria-label={`Installment ${i.installmentNumber} transaction number`}
                          />
                        ) : (
                          (i.transactionNo ?? '-')
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        {editing && row ? (
                          <Select
                            value={row.mode}
                            onChange={(e) => patchInstallment(idx, { mode: e.target.value })}
                            className="h-8 w-32 text-sm"
                            aria-label={`Installment ${i.installmentNumber} payment mode`}
                          >
                            <option value="">- None -</option>
                            {/* 'Due' only on the first installment - it starts
                                the client's service before payment, and service
                                starts once (ADR-053). */}
                            {paymentModesFor(i.installmentNumber).map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </Select>
                        ) : (
                          <StatusChip tone={i.paid ? 'success' : 'warning'}>
                            {i.paid ? 'Paid' : 'Due'}
                          </StatusChip>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <StatusChip tone={installmentBillingTone(i.billingLabel)}>
                          {i.billingLabel}
                        </StatusChip>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {editing && editable.payableTotal != null && (
            <p
              className={cn(
                'mt-2 text-[11px] font-semibold tabular-nums',
                scheduleMismatch ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {`Schedule totals ${inr(draftScheduleTotal)} · payable total ${inr(editable.payableTotal)}`}
              {scheduleMismatch ? ' - these must match to save.' : ''}
            </p>
          )}
        </Section>
      </div>

      <CorrectionNotice detail={detail} />

      <Section title="Internal Billing Notes">
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
          placeholder="Record comments or observations for the billing audit log…"
          className="min-h-[90px]"
        />
        <div className="mt-1 text-right text-[11px] text-muted-foreground tabular-nums">
          {`${notes.length}/1000`}
        </div>
      </Section>
    </div>
  );
};

// ─── Step 2: Invoice ─────────────────────────────────────────────────────────
const StepInvoice = ({ detail, onDone, locked }: StepProps) => {
  const { toast } = useToast();
  const record = useRecordInvoice(detail.id, detail.orderId);
  const s = detail.state5;
  const todayIso = new Date().toISOString().slice(0, 10);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [invoiceNumber, setInvoiceNumber] = useState(s.invoiceNumber ?? '');
  const [invoiceDate, setInvoiceDate] = useState(s.invoiceDate ?? todayIso);
  // Default the invoice amount to THIS installment's amount, not the lead total.
  const [invoiceAmount, setInvoiceAmount] = useState(
    s.invoiceAmount ?? detail.currentInstallmentAmount,
  );
  const [file, setFile] = useState<{ base64: string; name: string; size: number } | null>(null);

  const onPick = async (f: File | undefined) => {
    if (!f) return;
    if (f.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('The PDF exceeds the 10 MB limit');
      return;
    }
    const base64 = await fileToBase64(f);
    setFile({ base64, name: f.name, size: f.size });
  };

  const canSave = invoiceNumber.trim().length > 0 && invoiceAmount > 0 && !!file;
  const amountMismatch = invoiceAmount !== detail.currentInstallmentAmount;

  const submit = () => {
    if (!file) {
      toast.error('Upload the invoice PDF first');
      return;
    }
    record.mutate(
      {
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate,
        invoiceAmount,
        pdfBase64: file.base64,
        pdfName: file.name,
      },
      {
        onSuccess: () => {
          toast.success('Invoice saved');
          onDone();
        },
        onError: (error) => toast.error(errorMessage(error, 'Could not save the invoice')),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-extrabold">Generate Invoice</h2>
        <p className="text-sm text-muted-foreground">
          Upload the invoice PDF (generated in Zoho) and record its details.
        </p>
      </div>

      <Section title="Invoice Details">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Invoice Number</Label>
            <Input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="INV-2026-0001"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Invoice Date</Label>
            <Input
              type="date"
              value={invoiceDate}
              max={todayIso}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{`Invoice Amount · Installment ${detail.currentInstallmentNumber} = ${inr(detail.currentInstallmentAmount)}`}</Label>
            <NumberStepper
              value={invoiceAmount}
              onChange={setInvoiceAmount}
              min={0}
              max={1_000_000_000}
              step={100}
              inputWidthClassName="w-28"
              ariaLabel="Invoice amount"
            />
            {amountMismatch && (
              <p className="text-[11px] font-semibold text-warning">
                {`Differs from the installment amount by ${inr(Math.abs(invoiceAmount - detail.currentInstallmentAmount))}`}
              </p>
            )}
          </div>
        </div>

        <div className="mt-3 space-y-1.5">
          <Label>Invoice PDF</Label>
          {file && (
            <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2">
              <span className="inline-flex items-center gap-2 text-sm font-semibold">
                <FileText className="h-4 w-4" />
                {file.name}
                <span className="text-xs font-normal text-muted-foreground">{`${Math.round(file.size / 1024)} KB`}</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => setFile(null)}>
                Remove
              </Button>
            </div>
          )}
          {!file && s.hasInvoicePdf && (
            <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2">
              <span className="inline-flex items-center gap-2 text-sm font-semibold">
                <FileText className="h-4 w-4" />
                {s.invoicePdfName}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  void billingService.downloadInvoicePdf(detail.id, detail.orderId);
                }}
              >
                <Download className="mr-1 h-3.5 w-3.5" />
                Download
              </Button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              void onPick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Button
            variant="outline"
            size="sm"
            className="w-full border-dashed"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mr-1.5 h-4 w-4" />
            {file || s.hasInvoicePdf ? 'Replace PDF' : 'Upload the invoice PDF'}
          </Button>
        </div>
      </Section>

      {s.invoiceRecordedAt && (
        <p className="text-xs text-muted-foreground">
          {`Last saved ${fmtDateTime(s.invoiceRecordedAt)}${s.invoiceRecordedByName ? ` by ${s.invoiceRecordedByName}` : ''}.`}
        </p>
      )}

      <div className="flex justify-end">
        <Button size="sm" onClick={submit} disabled={!canSave || record.isPending || locked}>
          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
          {record.isPending ? 'Saving…' : 'Save Invoice'}
        </Button>
      </div>
    </div>
  );
};

// ─── Step 3: Notify ──────────────────────────────────────────────────────────
// The notification IS an email to the Service team - previewed and edited here
// exactly like the client invoice step, then sent. Sending it stamps the step.
const StepNotify = ({ detail, onDone, locked }: StepProps) => {
  const { toast } = useToast();
  const notify = useNotifyService(detail.id, detail.orderId);
  const s = detail.state5;
  const done = !!s.serviceNotifiedAt;

  // A due installment has no invoice step to have completed - verification
  // hands straight to this one (ADR-053).
  const ready = s.dueBilling ? !!s.verifiedAt : !!s.invoiceRecordedAt;

  const preview = useQuery({
    queryKey: [...QUERY_KEYS.BILLING.BY_ID(detail.id), detail.orderId, 'service-preview'],
    queryFn: () => billingService.previewServiceNotification(detail.id, detail.orderId),
    enabled: ready,
  });

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');

  useEffect(() => {
    if (preview.data?.defaultTo && !to) setTo(preview.data.defaultTo);
  }, [preview.data, to]);
  useEffect(() => {
    if (preview.data && !subject) setSubject(preview.data.subject);
  }, [preview.data, subject]);

  const submit = () => {
    if (!preview.data) return;
    const ccList = cc
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    notify.mutate(
      {
        to: [to.trim()],
        cc: ccList.length ? ccList : undefined,
        subject: subject || preview.data.subject,
        bodyHtml: preview.data.bodyHtml,
        bodyText: preview.data.bodyText,
      },
      {
        onSuccess: (r) => {
          toast.success(`Service queue ${r.detail.state5.serviceQueueRef ?? ''} notified`);
          onDone();
        },
        onError: (error) => toast.error(errorMessage(error, 'Could not notify the service team')),
      },
    );
  };

  if (!ready) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-extrabold">Service Team Notification</h2>
        <div className="rounded-lg border border-warning/40 bg-warning-tint p-3 text-sm text-ink">
          {s.dueBilling
            ? 'Verify the billing details before notifying the service team.'
            : 'Record the invoice in the Generate Invoice step before notifying the service team.'}
        </div>
      </div>
    );
  }

  const canSend = !!preview.data && to.trim().length > 0 && !notify.isPending && !locked;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-extrabold">Service Team Notification</h2>
          <p className="text-sm text-muted-foreground">
            Email the service team so they activate the subscribed products for this client.
          </p>
        </div>
        <Button size="sm" onClick={submit} disabled={!canSend}>
          <Wrench className="mr-1 h-3.5 w-3.5" />
          {(() => {
            if (notify.isPending) return 'Sending…';
            return done ? 'Resend To Service Team' : 'Send To Service Team';
          })()}
        </Button>
      </div>

      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-foreground">
        Billing only forwards this record to the Service Team queue. The Service Team assigns the
        executive and priority on their end.
      </div>

      <Section title="Handover Summary">
        <div className="grid gap-3 md:grid-cols-3">
          <ReadOnly label="Client" value={detail.clientName} />
          <ReadOnly label="ARN" value={detail.arn} />
          <ReadOnly label="Products" value={detail.products.map((p) => p.name).join(', ') || '-'} />
          {/* A due installment has no invoice by definition, so the summary says
              what it IS - unpaid, for this amount - rather than two bare dashes
              that read like missing data (ADR-053). */}
          <ReadOnly
            label={s.dueBilling ? 'Invoice' : 'Invoice Number'}
            value={s.dueBilling ? 'Not raised - installment unpaid' : (s.invoiceNumber ?? '-')}
          />
          <ReadOnly
            label={s.dueBilling ? 'Amount Due' : 'Invoice Amount'}
            value={(() => {
              if (s.dueBilling) return inr(detail.currentInstallmentAmount);
              return s.invoiceAmount != null ? inr(s.invoiceAmount) : '-';
            })()}
          />
          <ReadOnly label="Service Queue Ref" value={s.serviceQueueRef ?? '-'} />
        </div>
      </Section>

      <Section title="Email">
        <div className="grid gap-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>To (Service Team)</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>CC (comma separated)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} />
              {/* 'internal', not 'service': this step sends
                  service-notification-internal, whose template category is
                  'internal' - and the server merges the CC by that category. */}
              <LockedCcChips category="internal" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <p className="text-[11px] text-muted-foreground">
            The default recipient comes from the Service Activation template (Admin › Email
            Templates › Default To), falling back to the Service sender in Company Settings.
          </p>
        </div>
      </Section>

      <Section title="Preview">
        {preview.isLoading && <p className="text-sm text-muted-foreground">Loading preview…</p>}
        {preview.isError && (
          <p className="text-sm text-destructive">
            {errorMessage(preview.error, 'Could not load the service notification preview.')}
          </p>
        )}
        {preview.data && (
          <iframe
            title="Service notification preview"
            className="h-[360px] w-full rounded-md border border-border bg-white"
            srcDoc={preview.data.bodyHtml}
          />
        )}
      </Section>

      {done && s.serviceQueueRef && (
        <div className="flex items-start gap-3 rounded-lg border border-success/40 bg-success-tint p-4">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-success">
              Service Queue Entry Created
            </p>
            <p className="mt-1 font-mono text-base font-extrabold tracking-wider">
              {s.serviceQueueRef}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {`Notified ${fmtDateTime(s.serviceNotifiedAt)}${s.serviceNotifiedByName ? ` by ${s.serviceNotifiedByName}` : ''}.`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Step 4: Send ────────────────────────────────────────────────────────────
const StepSend = ({ detail, onDone, locked }: StepProps) => {
  const { toast } = useToast();
  const send = useSendClientInvoice(detail.id, detail.orderId);
  const s = detail.state5;
  const alreadySent = !!s.clientInvoiceSentAt;

  const preview = useQuery({
    queryKey: [...QUERY_KEYS.BILLING.BY_ID(detail.id), detail.orderId, 'invoice-preview'],
    queryFn: () => billingService.previewInvoiceEmail(detail.id, detail.orderId),
    enabled: !!s.hasInvoicePdf,
  });

  const [to, setTo] = useState(detail.email);
  const [cc, setCc] = useState(detail.rmEmail && detail.rmEmail !== '-' ? detail.rmEmail : '');
  const [subject, setSubject] = useState('');

  useEffect(() => {
    if (preview.data && !subject) setSubject(preview.data.subject);
  }, [preview.data, subject]);

  const canSend =
    !!s.hasInvoicePdf && !!preview.data && to.trim().length > 0 && !send.isPending && !locked;

  const sendLabel = (() => {
    if (send.isPending) return 'Sending…';
    return alreadySent ? 'Resend Email' : 'Send Email';
  })();

  const submit = () => {
    if (!preview.data) return;
    const ccList = cc
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    send.mutate(
      {
        templateSlug: INVOICE_TEMPLATE_SLUG,
        to: [to.trim()],
        cc: ccList.length ? ccList : undefined,
        subject: subject || preview.data.subject,
        bodyHtml: preview.data.bodyHtml,
        bodyText: preview.data.bodyText,
      },
      {
        onSuccess: () => {
          toast.success(`Invoice emailed to ${to.trim()}`);
          onDone();
        },
        onError: (error) => toast.error(errorMessage(error, 'Could not send the invoice email')),
      },
    );
  };

  if (!s.hasInvoicePdf) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-extrabold">Send Invoice To Client</h2>
        <div className="rounded-lg border border-warning/40 bg-warning-tint p-3 text-sm text-ink">
          Upload the invoice PDF in the Generate Invoice step before sending it to the client.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-extrabold">Send Invoice To Client</h2>
          <p className="text-sm text-muted-foreground">
            The uploaded invoice PDF is attached automatically.
          </p>
        </div>
        <Button size="sm" onClick={submit} disabled={!canSend}>
          <Send className="mr-1 h-3.5 w-3.5" />
          {sendLabel}
        </Button>
      </div>

      {alreadySent && (
        <p className="text-xs text-emerald-600">
          {`Invoice already sent ${fmtDateTime(s.clientInvoiceSentAt)}${s.clientInvoiceSentByName ? ` by ${s.clientInvoiceSentByName}` : ''}.`}
        </p>
      )}

      <Section title="Email">
        <div className="grid gap-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>CC (comma separated)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} />
              <LockedCcChips category="billing" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="inline-flex w-fit items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-semibold">
            <FileText className="h-4 w-4" />
            {s.invoicePdfName ?? 'Invoice PDF'}
            <span className="text-xs font-normal text-muted-foreground">(attached)</span>
          </div>
        </div>
      </Section>

      <Section title="Preview">
        {preview.isLoading && <p className="text-sm text-muted-foreground">Loading preview…</p>}
        {preview.isError && (
          <p className="text-sm text-destructive">
            {errorMessage(preview.error, 'Could not load the email preview.')}
          </p>
        )}
        {preview.data && (
          <iframe
            title="Invoice email preview"
            className="h-[360px] w-full rounded-md border border-border bg-white"
            srcDoc={preview.data.bodyHtml}
          />
        )}
      </Section>
    </div>
  );
};

// ─── Step 5: Complete ────────────────────────────────────────────────────────
const StepComplete = ({ detail, locked }: { detail: BillingClientDetail; locked: boolean }) => {
  const { toast } = useToast();
  const complete = useCompleteBilling(detail.id, detail.orderId);
  const s = detail.state5;
  const flags = doneFlags(detail);
  const done = !!s.completedAt;

  // Built from the steps this workflow actually runs. Listing one it skips
  // would leave the checklist permanently unsatisfied and the Complete button
  // dead - a due installment was never invoiced or sent, and a later one never
  // notified Service.
  const CHECK_LABEL: Record<StepKey, string> = {
    verify: 'Billing Verified',
    invoice: 'Invoice Generated',
    notify: 'Service Team Notified',
    send: 'Invoice Sent To Client',
    complete: 'Billing Complete',
  };
  const checks = stepsFor(detail)
    .filter((step) => step.key !== 'complete')
    .map((step) => ({ ok: flags[step.key], label: CHECK_LABEL[step.key] }));
  const allOk = checks.every((c) => c.ok);

  const submit = () => {
    complete.mutate(undefined, {
      onSuccess: () => toast.success('Billing completed'),
      onError: (error) => toast.error(errorMessage(error, 'Could not complete billing')),
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-extrabold">Billing Complete</h2>
        <p className="text-sm text-muted-foreground">Confirm closure once every step is done.</p>
      </div>

      {done ? (
        <div className="flex items-center gap-3 rounded-xl border border-success/30 bg-success-tint p-4">
          <CheckCircle2 className="h-6 w-6 shrink-0 text-success" />
          <div>
            <p className="text-sm font-bold text-success">Billing completed successfully.</p>
            <p className="text-xs text-muted-foreground">
              {`Completed ${fmtDateTime(s.completedAt)}${s.completedByName ? ` by ${s.completedByName}` : ''}.`}
            </p>
          </div>
        </div>
      ) : (
        <Section title="Pre-Completion Checklist">
          <ul className="space-y-2">
            {checks.map((c) => (
              <li key={c.label} className="flex items-center gap-2 text-sm">
                <span
                  className={cn(
                    'grid h-5 w-5 place-items-center rounded-full',
                    c.ok ? 'bg-success text-white' : 'bg-muted text-muted-foreground',
                  )}
                >
                  <CheckCircle2 className="h-3 w-3" />
                </span>
                <span className={cn(c.ok ? 'font-semibold' : 'text-muted-foreground')}>
                  {c.label}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Billing Summary">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <ReadOnly label="Client" value={detail.clientName} />
          <ReadOnly label="ARN" value={detail.arn} />
          {/* Nothing was invoiced on a due run, so the summary says so rather
              than showing dashes that read like missing data (ADR-053). */}
          <ReadOnly
            label={s.dueBilling ? 'Invoice' : 'Invoice Number'}
            value={s.dueBilling ? 'Not raised - installment unpaid' : (s.invoiceNumber ?? '-')}
          />
          <ReadOnly
            label={s.dueBilling ? 'Amount Due' : 'Invoice Amount'}
            value={(() => {
              if (s.dueBilling) return inr(detail.currentInstallmentAmount);
              return s.invoiceAmount != null ? inr(s.invoiceAmount) : '-';
            })()}
          />
          <ReadOnly label="Total Billed" value={inr(detail.total)} />
          <ReadOnly label="Service Queue" value={s.serviceQueueRef ?? '-'} />
        </div>
      </Section>

      {!done && (
        <div className="flex items-center justify-between rounded-lg border border-border bg-background p-4">
          <StatusChip tone="warning">Awaiting final confirmation</StatusChip>
          <Button size="sm" disabled={!allOk || complete.isPending || locked} onClick={submit}>
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
            {complete.isPending ? 'Completing…' : 'Mark Billing Complete'}
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── Claim-based assignment (pull model) ─────────────────────────────────────
// A billing task is claimed by one biller and worked exclusively. This panel
// lets the viewer pull an unclaimed task, release their own, or (as an admin)
// take over one claimed by someone else. It only shows while there is a pending
// installment to act on (detail.actionRequired).
interface AssignmentPanelProps {
  detail: BillingClientDetail;
  meId: number;
  isAdmin: boolean;
}

const AssignmentPanel = ({ detail, meId, isAdmin }: AssignmentPanelProps) => {
  const { toast } = useToast();
  const claim = useClaimBilling(detail.id, detail.orderId);
  const release = useReleaseBilling(detail.id, detail.orderId);

  if (!detail.actionRequired) return null;

  const { assignedToId, assignedToName } = detail;
  const mine = assignedToId != null && assignedToId === meId;
  const claimedByOther = assignedToId != null && assignedToId !== meId;
  const busy = claim.isPending || release.isPending;

  const doClaim = () =>
    claim.mutate(undefined, {
      onSuccess: () => toast.success('You claimed this billing task'),
      onError: (error) => toast.error(errorMessage(error, 'Could not claim this task')),
    });
  const doRelease = () =>
    release.mutate(undefined, {
      onSuccess: () => toast.success('Released to the unassigned pool'),
      onError: (error) => toast.error(errorMessage(error, 'Could not release this task')),
    });

  if (assignedToId == null) {
    return (
      <div className="mb-2 rounded-lg border border-warning/40 bg-warning-tint p-2.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-ink">Unclaimed</p>
        <p className="mb-2 text-[11px] text-ink">
          Claim this task to work it. Other billers won&apos;t be able to act on it while it&apos;s
          yours.
        </p>
        <Button size="sm" className="w-full" disabled={busy} onClick={doClaim}>
          <UserCheck className="mr-1 h-3.5 w-3.5" />
          {claim.isPending ? 'Claiming…' : 'Claim This Task'}
        </Button>
      </div>
    );
  }

  if (mine) {
    return (
      <div className="mb-2 rounded-lg border border-brand/30 bg-brand-tint p-2.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-brand">Claimed by you</p>
        <p className="mb-2 text-[11px] text-foreground">
          This task is in your queue. Release it if you want another biller to take over.
        </p>
        <Button size="sm" variant="outline" className="w-full" disabled={busy} onClick={doRelease}>
          {release.isPending ? 'Releasing…' : 'Release Task'}
        </Button>
      </div>
    );
  }

  // Claimed by someone else.
  return (
    <div className="mb-2 rounded-lg border border-border bg-muted/40 p-2.5">
      <p className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        <Lock className="h-3 w-3" />
        Claimed
      </p>
      <p className="mb-2 break-words text-[11px] text-foreground">
        {`This task is being handled by ${assignedToName ?? 'another biller'}. Actions are disabled${isAdmin ? '' : ' for you'}.`}
      </p>
      {isAdmin && claimedByOther && (
        <Button size="sm" variant="outline" className="w-full" disabled={busy} onClick={doClaim}>
          <UserCheck className="mr-1 h-3.5 w-3.5" />
          {claim.isPending ? 'Taking over…' : 'Take Over (Admin)'}
        </Button>
      )}
    </div>
  );
};

// ─── Shell ───────────────────────────────────────────────────────────────────
const BillingWorkflowPage = () => {
  const { id } = useParams<{ id: string }>();
  const leadId = Number(id);
  // The order this workflow bills, carried from the queue row that was clicked
  // (ADR-048). Absent on an older/bare link, in which case the server falls
  // back to the lead's current order exactly as before.
  const [searchParams] = useSearchParams();
  const orderIdParam = Number(searchParams.get('orderId'));
  const orderId = Number.isInteger(orderIdParam) && orderIdParam > 0 ? orderIdParam : undefined;
  const navigate = useNavigate();
  const { setExtra } = useBreadcrumb();
  const { user } = useAuth();
  const meId = user?.id ?? 0;
  const isAdmin = user?.role === 'admin';
  const { data: detail, isLoading, isError } = useBillingClient(leadId, orderId);

  const [active, setActive] = useState<StepKey>('verify');
  const [initialised, setInitialised] = useState(false);

  useEffect(() => {
    if (detail && !initialised) {
      setActive(stepsFor(detail)[firstIncompleteIndex(detail)].key);
      setInitialised(true);
    }
  }, [detail, initialised]);

  useEffect(() => {
    setExtra(detail ? detail.clientName : null);
    return () => setExtra(null);
  }, [detail, setExtra]);

  const back = () => {
    void navigate(ROUTES.BILLING_CLIENTS);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        Loading client…
      </div>
    );
  }
  if (isError || !detail) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">This client is not in the billing queue.</p>
        <Button variant="outline" size="sm" onClick={back}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to queue
        </Button>
      </div>
    );
  }

  const flags = doneFlags(detail);
  const steps = stepsFor(detail);
  const currentIdx = firstIncompleteIndex(detail);
  // A due workflow has no invoice steps, so `active` must never point at one -
  // it can still be holding a key from before the payment state changed.
  const activeStep = steps.some((s) => s.key === active) ? active : steps[currentIdx].key;
  // Locked for the viewer when the current installment is claimed by another
  // biller (admins may act on any task). The server enforces this too.
  const locked = detail.assignedToId != null && detail.assignedToId !== meId && !isAdmin;

  const stepState = (key: StepKey, idx: number): StepStatus => {
    if (flags[key]) return 'completed';
    if (idx === currentIdx) return 'current';
    return idx < currentIdx ? 'pending' : 'blocked';
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-5">
          <HeaderCell label="ID" value={String(detail.leadCode ?? detail.id)} mono />
          <HeaderCell label="Name" value={detail.clientName} />
          <HeaderCell label="ARN" value={detail.arn} mono />
          <HeaderCell label="Mobile" value={detail.mobile} />
          <HeaderCell label="Email" value={detail.email} />
        </div>
        <Button variant="outline" size="sm" onClick={back}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to queue
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <nav className="h-fit rounded-xl border border-border bg-card p-3 lg:sticky lg:top-4">
          <div className="flex items-center justify-between px-2 pb-2 pt-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Billing Workflow
            </p>
            <StatusChip tone={billingLeadTone(detail.billingStatus)}>
              {detail.billingStatus}
            </StatusChip>
          </div>

          <AssignmentPanel detail={detail} meId={meId} isAdmin={isAdmin} />

          {/* Which installment the workflow is currently billing (partial
              payments hand over installments one at a time, ADR-042). */}
          <div className="mb-2 rounded-lg border border-primary/30 bg-primary/5 p-2.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-primary">
              {detail.hasPendingInstallment ? 'Now billing' : 'Last billed'}
            </p>
            <p className="text-sm font-bold text-foreground">
              {`Installment ${detail.currentInstallmentNumber} of ${detail.installments.length} · ${inr(detail.currentInstallmentAmount)}`}
            </p>
            {!detail.hasPendingInstallment && (
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                All handed-over installments are billed. The next task appears when the RM hands
                over another installment.
              </p>
            )}
          </div>

          {/* Handover: the timeline's zeroth event, kept as a header since it is
              not one of the clickable steps. */}
          <div className="mb-2 rounded-lg border border-border bg-muted/30 p-2.5">
            <p className="font-mono text-[11px] tabular-nums text-foreground">
              {fmtDateTime(detail.handedOverAt)}
            </p>
            <p className="break-words text-xs font-semibold text-foreground">{detail.rmName}</p>
            <p className="text-sm font-bold text-foreground">Sales RM Handover</p>
            <p className="mt-0.5 break-words text-[11px] text-muted-foreground">
              {`ARN ${detail.arn} · ${detail.clientName}`}
            </p>
            {/* Which purchase is being billed - a returning client has several,
                each its own task, so the biller must see which one is open. */}
            <p className="mt-0.5 break-words text-[11px] font-semibold text-foreground">
              {`Order #${detail.orderNumber} · ${detail.orderType}`}
            </p>
          </div>

          {/* Says why the Service step is missing, so it does not read as a
              broken screen. */}
          {!detail.state5.notifiesService && (
            <div className="mb-2 rounded-lg border border-primary/30 bg-primary/5 p-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-primary">
                {`Installment ${detail.currentInstallmentNumber}`}
              </p>
              <p className="mt-1 text-[11px] text-foreground">
                The Service team was notified when installment 1 was billed and the client&rsquo;s
                service is already running, so there is nothing to hand them for this one. No new
                service queue row is raised.
              </p>
            </div>
          )}

          {/* Says why the two invoice steps are missing, likewise. */}
          {detail.state5.dueBilling && (
            <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5 dark:border-amber-900/40 dark:bg-amber-900/20">
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300">
                Unpaid - due
              </p>
              <p className="mt-1 text-[11px] text-amber-900 dark:text-amber-200">
                The client has not paid this installment yet. Service still starts, so verify the
                details and notify the Service team. There is nothing to invoice until the payment
                is recorded, and the installment comes back here in full once it is.
              </p>
            </div>
          )}

          <ol className="space-y-1">
            {steps.map((step, idx) => {
              const st = stepState(step.key, idx);
              const clickable = st !== 'blocked';
              const isActive = active === step.key;
              const Icon = STEP_ICON[st];
              const meta = stepMetaFor(detail, step.key);
              const isDone = st === 'completed';
              return (
                <li key={step.key}>
                  <button
                    type="button"
                    disabled={!clickable}
                    onClick={() => clickable && setActive(step.key)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-lg border p-2.5 text-left transition-colors',
                      isActive
                        ? 'border-primary bg-primary/5'
                        : 'border-transparent bg-background hover:border-border',
                      !clickable && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <span
                      className={cn(
                        'grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-bold',
                        STEP_DOT[st],
                      )}
                    >
                      {st === 'completed' || st === 'blocked' ? (
                        <Icon className="h-3.5 w-3.5" />
                      ) : (
                        idx + 1
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block text-sm font-bold', isActive && 'text-primary')}>
                        {step.title}
                      </span>
                      {isDone ? (
                        <>
                          <span className="mt-0.5 block font-mono text-[11px] tabular-nums text-muted-foreground">
                            {fmtDateTime(meta.at)}
                          </span>
                          {meta.by && (
                            <span className="block break-words text-[11px] font-semibold text-foreground">
                              {meta.by}
                            </span>
                          )}
                          {meta.detail && (
                            <span className="mt-0.5 block break-words text-[11px] leading-snug text-muted-foreground">
                              {meta.detail}
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
                            {st}
                          </span>
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">
                            {step.short}
                          </span>
                        </>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="min-w-0 rounded-xl border border-border bg-card p-5">
          {/* Each step hands to the next one THIS workflow runs, which is not
              always the next one in the list - a due installment skips the two
              invoice steps, and a later installment skips the Service notice. */}
          {activeStep === 'verify' && (
            <StepVerify
              detail={detail}
              onDone={() => setActive(nextStepAfter(detail, 'verify'))}
              locked={locked}
            />
          )}
          {activeStep === 'invoice' && (
            <StepInvoice
              detail={detail}
              onDone={() => setActive(nextStepAfter(detail, 'invoice'))}
              locked={locked}
            />
          )}
          {activeStep === 'notify' && (
            <StepNotify
              detail={detail}
              onDone={() => setActive(nextStepAfter(detail, 'notify'))}
              locked={locked}
            />
          )}
          {activeStep === 'send' && (
            <StepSend
              detail={detail}
              onDone={() => setActive(nextStepAfter(detail, 'send'))}
              locked={locked}
            />
          )}
          {activeStep === 'complete' && <StepComplete detail={detail} locked={locked} />}
        </div>
      </div>
    </div>
  );
};

export default BillingWorkflowPage;
